import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.js";
import { categoryLabels } from "../opportunities/api.js";
import {
  usePortalMe,
  usePortalProfile,
  useUpdateProfile,
  type PortalProfile as Profile,
} from "../portal/api.js";
import { ProfileFormFields, type ProfileFormState } from "../portal/ProfileFormFields.js";
import {
  cityLabelsForTownKeys,
  townKeysForZips,
  zipsForTownKeys,
} from "../serviceArea/qcServiceArea.js";

/**
 * Profile tab (unit 16): login identity, the linked business profile, and —
 * for APPROVED contractors — self-service editing including message-alert
 * preferences. Matching-data changes affect future invitations only.
 */
export function PortalProfile() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { data: me } = usePortalMe();
  const linked = Boolean(me?.contractor);
  const { data: profile } = usePortalProfile(linked);
  const [editing, setEditing] = useState(false);

  async function onSignOut() {
    await signOut();
    navigate("/", { replace: true });
  }

  return (
    <div className="mx-auto max-w-md px-6 py-10">
      <h1 className="text-xl font-bold text-charcoal">Profile</h1>

      <div className="mt-6 rounded-2xl border border-charcoal/15 bg-white p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal">Account</h2>
        <p className="mt-2 text-base text-charcoal">{me?.email ?? "—"}</p>
        {me && !linked && (
          <p className="mt-1 text-sm text-charcoal">
            Not linked to a pro profile yet.{" "}
            <Link to="/portal/apply" className="font-semibold text-brand-700">
              Apply to get matched
            </Link>
          </p>
        )}
      </div>

      {profile && !editing && (
        <ProfileSummary
          profile={profile}
          onEdit={profile.status === "APPROVED" ? () => setEditing(true) : undefined}
        />
      )}
      {profile && editing && <ProfileEditor profile={profile} onDone={() => setEditing(false)} />}

      <button
        type="button"
        onClick={() => void onSignOut()}
        className="mt-6 inline-flex min-h-[44px] w-full items-center justify-center rounded-xl border border-charcoal/30 bg-white px-6 text-base font-semibold text-charcoal shadow-sm active:bg-field-gray"
      >
        Sign out
      </button>
    </div>
  );
}

function ProfileSummary({ profile, onEdit }: { profile: Profile; onEdit?: () => void }) {
  return (
    <div className="mt-4 rounded-2xl border border-charcoal/15 bg-white p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal">Business</h2>
          <p className="mt-2 text-base font-semibold text-charcoal">{profile.businessName}</p>
          <p className="text-sm text-charcoal">{profile.contactName}</p>
        </div>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="min-h-[44px] rounded-xl border border-brand-700 px-4 text-sm font-semibold text-brand-700 active:bg-brand-50"
          >
            Edit
          </button>
        ) : (
          <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
            Under review
          </span>
        )}
      </div>

      <dl className="mt-4 space-y-3 text-sm">
        <Row label="Email">{profile.email}</Row>
        <Row label="Phone">{profile.phone}</Row>
        {profile.website && <Row label="Website">{profile.website}</Row>}
        <Row label="Services">
          {profile.serviceCategories.map((c) => categoryLabels[c] ?? c).join(", ")}
        </Row>
        <Row label="Service area">
          {profile.serviceArea.cities.join(", ") ||
            (profile.serviceArea.postalCodes.length > 0
              ? `${profile.serviceArea.postalCodes.length} ZIP codes`
              : "—")}
        </Row>
        <Row label="Message alerts">{alertLabels[profile.messageAlerts]}</Row>
      </dl>
      {!onEdit && (
        <p className="mt-4 text-xs text-charcoal">
          Profile editing unlocks once your application is approved.
        </p>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-28 shrink-0 font-medium text-charcoal">{label}</dt>
      <dd className="text-charcoal">{children}</dd>
    </div>
  );
}

const alertLabels = {
  immediate: "Email me right away",
  digest: "Daily summary",
  off: "Off",
} as const;

function ProfileEditor({ profile, onDone }: { profile: Profile; onDone: () => void }) {
  const update = useUpdateProfile();
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<ProfileFormState>({
    businessName: profile.businessName,
    contactName: profile.contactName,
    email: profile.email,
    phone: profile.phone,
    website: profile.website ?? "",
    yearsExperience: String(profile.yearsExperience),
    serviceCategories: profile.serviceCategories,
    // Recover the selected towns from the stored ZIP set so the map opens
    // pre-filled with the contractor's current area.
    selectedTowns: townKeysForZips(profile.serviceArea.postalCodes),
    licenseInfo: profile.licenseInfo ?? "",
    insuranceInfo: profile.insuranceInfo ?? "",
    about: profile.about ?? "",
  });
  const [messageAlerts, setMessageAlerts] = useState(profile.messageAlerts);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (state.serviceCategories.length === 0) {
      setError("Pick at least one service you offer.");
      return;
    }
    if (state.selectedTowns.length === 0) {
      setError("Keep at least one town in your service area.");
      return;
    }

    try {
      await update.mutateAsync({
        businessName: state.businessName.trim(),
        contactName: state.contactName.trim(),
        phone: state.phone.trim(),
        ...(state.website.trim() ? { website: state.website.trim() } : {}),
        serviceCategories: state.serviceCategories,
        serviceArea: {
          postalCodes: zipsForTownKeys(state.selectedTowns),
          cities: cityLabelsForTownKeys(state.selectedTowns),
        },
        ...(state.licenseInfo.trim() ? { licenseInfo: state.licenseInfo.trim() } : {}),
        ...(state.insuranceInfo.trim() ? { insuranceInfo: state.insuranceInfo.trim() } : {}),
        ...(state.about.trim() ? { about: state.about.trim() } : {}),
        messageAlerts,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save your changes");
    }
  }

  return (
    <form
      className="mt-4 space-y-5 rounded-2xl border border-charcoal/15 bg-white p-6"
      onSubmit={(e) => void onSubmit(e)}
      noValidate
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal">
        Edit business profile
      </h2>
      <p className="text-xs text-charcoal">
        Changes to services and service area apply to future job invitations.
      </p>

      <ProfileFormFields
        state={state}
        onChange={(patch) => setState((s) => ({ ...s, ...patch }))}
        showIdentity={false}
      />

      <fieldset>
        <legend className="mb-2 block text-sm font-medium text-charcoal">New-message alerts</legend>
        <div className="space-y-2">
          {(Object.keys(alertLabels) as (keyof typeof alertLabels)[]).map((mode) => (
            <label
              key={mode}
              className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-xl border border-charcoal/30 px-3 text-sm font-medium text-charcoal"
            >
              <input
                type="radio"
                name="messageAlerts"
                checked={messageAlerts === mode}
                onChange={() => setMessageAlerts(mode)}
                className="h-4 w-4 accent-brand-700"
              />
              {alertLabels[mode]}
            </label>
          ))}
        </div>
      </fieldset>

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onDone}
          className="min-h-[44px] flex-1 rounded-xl border border-charcoal/30 bg-white text-sm font-semibold text-charcoal active:bg-field-gray"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={update.isPending}
          className="min-h-[44px] flex-1 rounded-xl bg-brand-700 text-sm font-semibold text-white active:bg-brand-800 disabled:opacity-60"
        >
          {update.isPending ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}
